import type { ToolInput } from "./types";

type HandlerFn = (input: ToolInput) => any;
type AsyncHandlerFn = (input: ToolInput) => Promise<any>;

/**
 * Creates a sync category handler that dispatches to the correct operation handler.
 * The `operation` field in the input determines which handler to call.
 */
export function createCategoryHandler(handlers: Record<string, HandlerFn>): HandlerFn {
  return (input: ToolInput) => {
    const { operation } = input;
    if (!operation) return { error: "Missing required 'operation' parameter" };
    const handler = handlers[operation];
    if (!handler) return { error: `Unknown operation: '${operation}'` };
    return handler(input);
  };
}

/**
 * Creates an async category handler that dispatches to the correct operation handler.
 * Used when any operation in the category is async.
 */
export function createAsyncCategoryHandler(
  syncHandlers: Record<string, HandlerFn>,
  asyncHandlers: Record<string, AsyncHandlerFn>
): AsyncHandlerFn {
  return async (input: ToolInput) => {
    const { operation } = input;
    if (!operation) return { error: "Missing required 'operation' parameter" };
    const asyncHandler = asyncHandlers[operation];
    if (asyncHandler) return asyncHandler(input);
    const syncHandler = syncHandlers[operation];
    if (syncHandler) return syncHandler(input);
    console.warn(
      `[CategoryDispatcher] Unknown operation: '${operation}'. Available: sync=[${Object.keys(syncHandlers)}] async=[${Object.keys(asyncHandlers)}]`
    );
    return { error: `Unknown operation: '${operation}'` };
  };
}
